import React, { useState, useEffect } from 'react';
import { Search, Filter, Phone, Mail, CheckCircle, Wrench, Settings, Truck, Shield, Clock, X } from 'lucide-react';
import SEO from '../components/SEO';
import { useContent } from '../hooks/useContent';

const SpecificPartsPage = () => {
  const { content: heroImages } = useContent('heroImages');
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCondition, setSelectedCondition] = useState('all');
  const [partsData, setPartsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedPart, setSelectedPart] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: 'Custom Parts Quote Request',
    message: 'I would like to request a custom quote for MAN truck parts. Please contact me with more details.'
  });

  // Fetch parts from API
  useEffect(() => {
    const fetchParts = async () => {
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/parts`);
        if (response.ok) {
          const data = await response.json();
          setPartsData(data);
        } else {
          throw new Error('Failed to fetch parts');
        }
      } catch (error) {
        console.error('Error fetching parts:', error);
        setError('Failed to load parts data');
      } finally {
        setLoading(false);
      }
    };

    fetchParts();
  }, []);

  // Fallback static data for development
  const fallbackPartsData = [
    // Engine Parts
    {
      id: "ENG001",
      name: "MAN D2676 Complete Engine Assembly",
      category: "Engine",
      partNumber: "51.01001-7484",
      condition: "Used - Excellent",
      compatibility: "MAN TGX, TGS, TGA Series",
      description: "Complete 12.4L Euro-6 engine assembly. Low mileage, fully tested with compression report. Includes turbocharger, ECU, and wiring harness.",
      price: "£12,500",
      availability: "In Stock",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },
    {
      id: "ENG002", 
      name: "MAN D2066 Turbocharger",
      category: "Engine",
      partNumber: "51.09100-7630",
      condition: "Genuine New",
      compatibility: "MAN TGL, TGM Series",
      description: "Genuine MAN turbocharger for D2066 engines. Brand new OEM part with full manufacturer warranty.",
      price: "£2,850",
      availability: "In Stock",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },
    {
      id: "ENG003",
      name: "MAN Common Rail Fuel Pump",
      category: "Engine", 
      partNumber: "51.11103-7058",
      condition: "Reconditioned",
      compatibility: "MAN TGX 18.440, 18.480 Euro-6",
      description: "High-pressure fuel pump for common rail injection system. Professionally reconditioned with new seals and tested to OEM specifications.",
      price: "£1,650",
      availability: "2-3 Days",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },

    // Transmission Parts
    {
      id: "TRN001",
      name: "MAN TipMatic Gearbox - 12AS2301",
      category: "Transmission",
      partNumber: "81.32004-6421", 
      condition: "Used - Good",
      compatibility: "MAN TGX, TGS Series",
      description: "12-speed automated manual transmission. Fully inspected and tested. Includes control unit and wiring.",
      price: "£8,500",
      availability: "In Stock",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },
    {
      id: "TRN002",
      name: "MAN Clutch Assembly Kit",
      category: "Transmission",
      partNumber: "81.30360-0249",
      condition: "Genuine New",
      compatibility: "MAN TGL 8.180, 10.180, 12.180",
      description: "Complete clutch kit including pressure plate, friction disc, and release bearing. Genuine MAN parts.",
      price: "£950",
      availability: "In Stock", 
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },

    // Brake System Parts
    {
      id: "BRK001",
      name: "MAN Brake Disc Set - Front Axle",
      category: "Brakes",
      partNumber: "81.50803-0155",
      condition: "Genuine New",
      compatibility: "MAN TGX, TGS, TGA Series", 
      description: "Heavy-duty brake discs for front axle. Ventilated design for optimal heat dissipation. Sold as a pair.",
      price: "£485",
      availability: "In Stock",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },
    {
      id: "BRK002",
      name: "Air Brake Compressor",
      category: "Brakes",
      partNumber: "51.54100-7285",
      condition: "Reconditioned",
      compatibility: "MAN TGX, TGS Euro-6",
      description: "Twin-cylinder air brake compressor. Professionally reconditioned with new seals and valves.",
      price: "£1,250",
      availability: "In Stock", 
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },

    // Electrical Parts
    {
      id: "ELE001",
      name: "MAN ECU Control Unit", 
      category: "Electrical",
      partNumber: "51.25803-7406",
      condition: "Used - Tested",
      compatibility: "MAN TGX 18.440 Euro-6",
      description: "Engine control unit with latest software version. Fully tested and programmed. MANCAT3 configuration included.",
      price: "£1,850",
      availability: "In Stock",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },
    {
      id: "ELE002",
      name: "MAN Alternator 28V/100A",
      category: "Electrical",
      partNumber: "51.26101-7203",
      condition: "Reconditioned",
      compatibility: "MAN TGL, TGM, TGX Series",
      description: "Heavy-duty alternator for commercial vehicles. Professionally reconditioned with new bearings and brushes.",
      price: "£425",
      availability: "In Stock",
      warranty: "Up to 12 months", 
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },

    // Cab & Body Parts
    {
      id: "CAB001",
      name: "MAN TGX Cab Door - Driver Side",
      category: "Cab & Body",
      partNumber: "81.62601-6840",
      condition: "Used - Good",
      compatibility: "MAN TGX Euro-6 (2013-2020)",
      description: "Complete cab door assembly with window, mirror mounting, and internal trim. Minor scuffs, fully functional.",
      price: "£650",
      availability: "In Stock",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },
    {
      id: "CAB002",
      name: "MAN Driver Seat Assembly",
      category: "Cab & Body", 
      partNumber: "81.62001-6420",
      condition: "Used - Very Good",
      compatibility: "MAN TGX, TGS Series",
      description: "Air-suspended driver seat with heating and adjustment controls. Minimal wear, excellent comfort.",
      price: "£750",
      availability: "In Stock",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },

    // Suspension Parts
    {
      id: "SUS001",
      name: "MAN Front Axle Leaf Spring Set",
      category: "Suspension",
      partNumber: "81.41410-6125",
      condition: "Used - Good",
      compatibility: "MAN TGL, TGM Series",
      description: "Complete leaf spring assembly for front axle. Inspected and tested. Some surface rust but structurally sound.",
      price: "£350",
      availability: "In Stock",
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    },
    {
      id: "SUS002",
      name: "Air Suspension Bellows - Rear",
      category: "Suspension",
      partNumber: "81.43710-6890",
      condition: "Genuine New",
      compatibility: "MAN TGX, TGS Series",
      description: "OEM air suspension bellows for rear axle. Brand new genuine MAN part with full warranty.",
      price: "£285",
      availability: "In Stock", 
      warranty: "Up to 12 months",
      image: "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'Engine', label: 'Engine Parts' },
    { value: 'Transmission', label: 'Transmission' },
    { value: 'Brakes', label: 'Brake System' },
    { value: 'Electrical', label: 'Electrical' },
    { value: 'Cab & Body', label: 'Cab & Body' },
    { value: 'Suspension', label: 'Suspension' }
  ];

  const conditions = [
    { value: 'all', label: 'All Conditions' },
    { value: 'Genuine New', label: 'Genuine New' },
    { value: 'Reconditioned', label: 'Reconditioned' },
    { value: 'Used - Excellent', label: 'Used - Excellent' },
    { value: 'Used - Very Good', label: 'Used - Very Good' },
    { value: 'Used - Good', label: 'Used - Good' }
  ];

  const filteredParts = (partsData.length > 0 ? partsData : fallbackPartsData).filter(part => {
    const matchesSearch = part.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         part.partNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         part.compatibility.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || part.category === selectedCategory;
    const matchesCondition = selectedCondition === 'all' || part.condition.startsWith(selectedCondition);
    
    return matchesSearch && matchesCategory && matchesCondition;
  });

  const getConditionColor = (condition) => {
    if (condition === 'Genuine New') return 'text-green-600 bg-green-50';
    if (condition.startsWith('Reconditioned')) return 'text-blue-600 bg-blue-50';
    if (condition.includes('Excellent')) return 'text-purple-600 bg-purple-50';
    if (condition.includes('Very Good')) return 'text-indigo-600 bg-indigo-50';
    return 'text-gray-600 bg-gray-50';
  };

  const getAvailabilityColor = (availability) => {
    if (availability === 'In Stock') return 'text-green-600 bg-green-50';
    return 'text-yellow-600 bg-yellow-50';
  };

  const handleEnquireNow = (part) => {
    setSelectedPart(part);
    setContactForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: `Enquiry about ${part.name} - Part #${part.partNumber}`,
      message: `Hi,\n\nI am interested in the following part:\n\n${part.name}\nPart Number: ${part.partNumber}\nPrice: ${part.price}\nCompatibility: ${part.compatibility}\n\nPlease provide more information and availability.\n\nThanks`
    });
    setShowContactModal(true);
  };

  const handleContactFormChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleContactFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitMessage('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm)
      });

      if (response.ok) {
        setSubmitMessage('Your enquiry has been sent successfully! We will get back to you soon.');
        setContactForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: '',
          message: ''
        });
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowContactModal(false);
          setSubmitMessage('');
        }, 2000);
      } else {
        throw new Error('Failed to send enquiry');
      }
    } catch (error) {
      console.error('Error sending enquiry:', error);
      setSubmitMessage('Failed to send enquiry. Please try again or contact us directly at sales@lawmancommercials.com');
    } finally {
      setSubmitLoading(false);
    }
  };

  const closeModal = () => {
    setShowContactModal(false);
    setSelectedPart(null);
    setSubmitMessage('');
  };

  const handleQuoteFormChange = (e) => {
    setQuoteForm({
      ...quoteForm,
      [e.target.name]: e.target.value
    });
  };

  const handleQuoteFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setSubmitMessage('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/auth/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteForm)
      });

      if (response.ok) {
        setSubmitMessage('Your quote request has been sent successfully! We will get back to you soon.');
        setQuoteForm({
          name: '',
          email: '',
          phone: '',
          company: '',
          subject: 'Custom Parts Quote Request',
          message: 'I would like to request a custom quote for MAN truck parts. Please contact me with more details.'
        });
        // Close modal after 2 seconds
        setTimeout(() => {
          setShowQuoteModal(false);
          setSubmitMessage('');
        }, 2000);
      } else {
        throw new Error('Failed to send quote request');
      }
    } catch (error) {
      console.error('Error sending quote request:', error);
      setSubmitMessage('Failed to send quote request. Please try again or contact us directly at sales@lawmancommercials.com');
    } finally {
      setSubmitLoading(false);
    }
  };

  const closeQuoteModal = () => {
    setShowQuoteModal(false);
    setSubmitMessage('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="MAN Truck Parts"
        description="Genuine and OEM MAN truck parts in Leicester. Engine parts, transmission, brakes, electrical, cab & body components. Up to 12 months coverage. Quality guaranteed parts with fast UK delivery."
        keywords="MAN truck parts, genuine MAN parts, OEM truck parts Leicester, MAN engine parts, MAN transmission parts, commercial vehicle parts"
        canonical="/parts"
      />
      
      {/* Hero Section */}
      <section className="relative bg-black text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 to-black/70"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('${heroImages?.partsPage || 'https://customer-assets.emergentagent.com/job_radius-consistency/artifacts/smlx0ylx_truckEngine.webp'}')`
          }}
        ></div>
        
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 font-inter">
            MAN Truck Parts Catalogue
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto font-inter">
            Genuine, reconditioned and quality used MAN truck parts. From engines to electrical components - we have what you need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded font-bold transition-colors shadow-lg"
              onClick={() => document.getElementById('parts-catalogue').scrollIntoView({ behavior: 'smooth' })}
            >
              Search Parts
            </button>
            <button 
              className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-3 rounded font-bold transition-colors shadow-lg"
              onClick={() => setShowQuoteModal(true)}
            >
              Request Custom Quote
            </button>
          </div>
        </div>
      </section>

      {/* Search & Filters */}
      <section id="parts-catalogue" className="py-8 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800 mb-2 font-inter">Find MAN Parts</h2>
            <p className="text-gray-600 font-inter">Search our extensive inventory of genuine and OEM MAN truck parts</p>
          </div>
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search */}
            <div className="flex-1 min-w-64">
              <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Search Parts Catalogue</label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Enter part name, number, or vehicle compatibility..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Part Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            {/* Condition Filter */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2 font-inter">Part Condition</label>
              <select
                value={selectedCondition}
                onChange={(e) => setSelectedCondition(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {conditions.map(cond => (
                  <option key={cond.value} value={cond.value}>{cond.label}</option>
                ))}
              </select>
            </div>

            <div className="text-sm text-gray-600">
              {filteredParts.length} parts found
            </div>
          </div>
        </div>
      </section>

      {/* Parts Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading parts...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12 bg-red-50 rounded-lg">
              <p className="text-red-600 font-bold">{error}</p>
              <p className="text-gray-600 mt-2">Showing fallback data instead.</p>
            </div>
          ) : null}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredParts.map((part) => (
              <div key={part.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative">
                  <img 
                    src={part.images && part.images.length > 0 
                      ? part.images[0] 
                      : "https://customer-assets.emergentagent.com/job_truckdealership/artifacts/7kr9f0qz_truckImage.png"
                    } 
                    alt={part.name}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${getConditionColor(part.condition)}`}>
                      {part.condition}
                    </span>
                  </div>
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${getAvailabilityColor(part.availability)}`}>
                      {part.availability}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-black mb-2 font-inter line-clamp-2">
                    {part.name}
                  </h3>
                  
                  <div className="space-y-2 mb-4 text-sm text-gray-600">
                    <div><strong>Part #:</strong> {part.partNumber}</div>
                    <div><strong>Fits:</strong> {part.compatibility}</div>
                    <div><strong>Coverage:</strong> {part.warranty}</div>
                  </div>

                  <p className="text-gray-600 mb-4 text-sm line-clamp-3">
                    {part.description}
                  </p>

                  <div className="flex justify-between items-center">
                    <div className="text-2xl font-bold text-black">
                      {part.price}
                    </div>
                    <button 
                      className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded font-bold transition-colors"
                      onClick={() => handleEnquireNow(part)}
                    >
                      Enquire Now
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredParts.length === 0 && (
            <div className="text-center py-12">
              <Wrench className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-700 mb-2">No parts found</h3>
              <p className="text-gray-600">Try adjusting your search criteria or contact us for specific requirements.</p>
            </div>
          )}
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-black mb-4 font-inter">
              Our Parts Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional parts sourcing and supply services for MAN commercial vehicles
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-black mb-2">Quality Guaranteed</h3>
              <p className="text-gray-600 text-sm">All parts tested and guaranteed for quality and compatibility</p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-black mb-2">Fast Delivery</h3>
              <p className="text-gray-600 text-sm">Next day delivery available for in-stock items across UK</p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-black mb-2">Warranty Included</h3>
              <p className="text-gray-600 text-sm">Comprehensive warranty on all reconditioned and new parts</p>
            </div>

            <div className="text-center">
              <div className="bg-orange-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <Wrench className="h-8 w-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-black mb-2">Expert Support</h3>
              <p className="text-gray-600 text-sm">MAN specialists available for parts identification and advice</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6 font-inter">
            Can't Find the Part You Need?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Our MAN parts specialists can source any component you need. Contact us with your requirements.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <Phone className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Parts Enquiry Line</h3>
              <p className="text-orange-500 font-bold">01530 231 235</p>
            </div>
            
            <div className="text-center">
              <Mail className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Email Your Requirements</h3>
              <p className="text-orange-500 font-bold">sales@lawmancommercials.com</p>
            </div>
            
            <div className="text-center">
              <Wrench className="h-12 w-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">Expert Advice</h3>
              <p className="text-orange-500 font-bold">30+ Years MAN Experience</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-black">Part Enquiry</h2>
                <button 
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 p-1"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {selectedPart && (
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h3 className="font-bold text-lg text-black mb-2">{selectedPart.name}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600">
                    <div><strong>Part Number:</strong> {selectedPart.partNumber}</div>
                    <div><strong>Price:</strong> {selectedPart.price}</div>
                    <div><strong>Condition:</strong> {selectedPart.condition}</div>
                    <div><strong>Compatibility:</strong> {selectedPart.compatibility}</div>
                  </div>
                </div>
              )}

              {submitMessage && (
                <div className={`p-4 rounded-lg mb-4 ${
                  submitMessage.includes('successfully') 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}

              <form onSubmit={handleContactFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleContactFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={contactForm.company}
                      onChange={handleContactFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={contactForm.subject}
                    onChange={handleContactFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={handleContactFormChange}
                    required
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitLoading ? 'Sending...' : 'Send Enquiry'}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded font-bold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Quote Modal */}
      {showQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-black">Custom Parts Quote</h2>
                <button 
                  onClick={closeQuoteModal}
                  className="text-gray-500 hover:text-gray-700 p-1"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="font-bold text-lg text-black mb-2">Custom Parts Quote Request</h3>
                <p className="text-gray-600">Request a custom quote for specific MAN truck parts, bulk orders, or hard-to-find components.</p>
              </div>

              {submitMessage && (
                <div className={`p-4 rounded-lg mb-4 ${
                  submitMessage.includes('successfully') 
                    ? 'bg-green-50 text-green-800 border border-green-200' 
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}

              <form onSubmit={handleQuoteFormSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={quoteForm.name}
                      onChange={handleQuoteFormChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={quoteForm.email}
                      onChange={handleQuoteFormChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={quoteForm.phone}
                      onChange={handleQuoteFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={quoteForm.company}
                      onChange={handleQuoteFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={quoteForm.subject}
                    onChange={handleQuoteFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Parts Requirements *
                  </label>
                  <textarea
                    name="message"
                    value={quoteForm.message}
                    onChange={handleQuoteFormChange}
                    required
                    rows={6}
                    placeholder="Please describe the parts you need, quantities, vehicle compatibility, and any specific requirements..."
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="submit"
                    disabled={submitLoading}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitLoading ? 'Sending...' : 'Request Quote'}
                  </button>
                  <button
                    type="button"
                    onClick={closeQuoteModal}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 px-6 py-2 rounded font-bold transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecificPartsPage;